import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { CheckList } from "@/components/CheckList";
import { linesForUser } from "@/hooks/useOperatorScope";

const EMPTY = { name: "", username: "", password: "", role: "operator", machine_ids: [] };

function fromUser(user, machines) {
  return { name: user.name || "", username: user.username || "", role: user.role, password: "", machine_ids: linesForUser(machines, user.id).map((m) => m.id) };
}

// mode "add" (user == null) or "edit" (user set). Testids differ per mode to keep existing tests stable.
export default function UserFormDialog({ open, user, currentUser, machines = [], onOpenChange, onSubmit }) {
  const isEdit = !!user;
  const p = isEdit ? "user-edit" : "user-form";
  const [form, setForm] = useState(EMPTY);
  useEffect(() => {
    if (open) setForm(user ? fromUser(user, machines) : EMPTY);
  }, [open, user, machines, setForm]);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const toggleLine = (id) => setForm((f) => ({ ...f, machine_ids: f.machine_ids.includes(id) ? f.machine_ids.filter((x) => x !== id) : [...f.machine_ids, id] }));
  const lockedRole = isEdit && user.id === currentUser.id;
  const lineItems = [...machines].sort((a, b) => a.line_code.localeCompare(b.line_code)).map((m) => ({ id: m.id, label: `${m.line_code} · ${m.name}`, hint: `${(m.operators || []).length} operator${(m.operators || []).length !== 1 ? "s" : ""}` }));

  const submit = async (e) => {
    e.preventDefault();
    const patch = { name: form.name, username: form.username.trim().toLowerCase(), role: form.role };
    if (form.password) patch.password = form.password;
    if (form.role === "operator") patch.machine_ids = form.machine_ids; // non-operators are cleared server-side
    await onSubmit(patch, user);
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid={`${p}-modal`}>
        <DialogHeader><DialogTitle className="font-display">{isEdit ? "Edit User" : "Add New User"}</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div className="space-y-1.5">
            <Label>Full Name</Label>
            <Input value={form.name} onChange={(e) => set("name", e.target.value)} required data-testid={`${p}-name`} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>Username</Label>
              <Input value={form.username} autoCapitalize="none" autoCorrect="off" onChange={(e) => set("username", e.target.value)} required minLength={3} data-testid={`${p}-username`} />
            </div>
            <div className="space-y-1.5">
              <Label>{isEdit ? "New Password" : "Password"}</Label>
              <Input type="password" placeholder={isEdit ? "leave blank to keep" : undefined} value={form.password} onChange={(e) => set("password", e.target.value)} required={!isEdit} minLength={6} data-testid={`${p}-password`} />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label>Role</Label>
            <Select value={form.role} onValueChange={(v) => set("role", v)} disabled={lockedRole}>
              <SelectTrigger data-testid={`${p}-role`}><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="admin">Administrator</SelectItem>
                <SelectItem value="planner">Production Planner</SelectItem>
                <SelectItem value="operator">Operator / Viewer</SelectItem>
              </SelectContent>
            </Select>
            {lockedRole && <p className="text-[11px] text-slate-400">You can't change your own role — ask another admin.</p>}
          </div>
          {form.role === "operator" && (
            <div className="space-y-1.5" data-testid={`${p}-lines`}>
              <Label>Assigned Lines <span className="text-slate-400 font-normal">({form.machine_ids.length} selected)</span></Label>
              <CheckList items={lineItems} selected={new Set(form.machine_ids)} onToggle={toggleLine} testidPrefix={`${p}-line`} maxHeight="max-h-44"
                searchPlaceholder="Search lines…" emptyText="No production lines yet — add one on the Lines tab." />
              <p className="text-[11px] text-slate-400">The operator's dashboard shows only jobs and machines on these lines.</p>
            </div>
          )}
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button type="submit" className="bg-blue-600 hover:bg-blue-700" data-testid={`${p}-submit`}>{isEdit ? "Save Changes" : "Create User"}</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
